// import React, { ReactNode } from "react";
// import { Navigate, useLocation, useNavigate } from "react-router-dom";
// import config from "../config/config";
// import { useSiteContext } from "../contexts/SiteProvider";
// import { setSessionStorage } from "../services/sessionService";
// import { showAlertAutoClose, showNotification1 } from "../services/notifyService";

// interface PrivateRouteProps {
//   children: ReactNode;
//   allowedRoles?: string[];
// }

// const session_key = config.REDIRECT_SESSION_KEY || "session_key";

// const flattenNav = (sideNav: any[]) => (
//   sideNav.reduce((acc: any[], item: any) => {
//     acc.push(item);
//     if (item?.children && item?.children.length > 0) acc.push(...item.children);
//     return acc;
//   }, [])
//   .map((i: any) => ({ ...i, roles: i.roles.split(",") }))
// );

// const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
//   const { user, sideNav, closeModal } = useSiteContext();
//   const location = useLocation();
//   const navigate = useNavigate();
//   let hasPermission = false

//   const currentPath = location.pathname;

//   // User not logged in
//   if (!user || Object.values(user).length < 1) {
//     setSessionStorage(session_key, currentPath);
//     showAlertAutoClose("Please Login", "error");
//     closeModal("all")
//     return <Navigate to="/login" />;
//   }

//   // Check role-based access
//   const flatNav = flattenNav(sideNav);
//   const currentNavObj = flatNav.find((i: any) => i?.link === location.pathname);

//   if (!currentNavObj) {
//     return <>{children}</>; // Route is not in nav config, allow
//   }

//   const userRoles = user?.roles ?? [];
//   const navRoles = currentNavObj?.roles ?? [];
//   hasPermission = userRoles.some((r: string) => navRoles.includes(r));

//   if (!hasPermission) {
//     showNotification1("error", "Restricted Access");

//     // If you want to navigate back instead of a fixed redirect, use:
//     navigate(-1);

//     // Or:
//     // return <Navigate to="/auth/dashboard-user" replace />;

//     return null; // Important: ensure something is returned
//   }

//   // Clear session key and render allowed content
//   setSessionStorage(session_key, "");
//   return hasPermission ? <>{children}</> : <></>;
// };

// export default React.memo(PrivateRoute);



import React, { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../config/config";
import { useSiteContext } from "../contexts/SiteProvider";
import { showNotification1 } from "../services/notifyService";
import { setSessionStorage } from "../services/sessionService";

interface PrivateRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

const session_key = config.REDIRECT_SESSION_KEY || "session_key";

const flattenNav = (sideNav: any[]) =>
  sideNav
    .reduce((acc: any[], item: any) => {
      acc.push(item);
      if (item?.children && Array.isArray(item.children) && item.children.length > 0) {
        acc.push(...item.children);
      }
      return acc;
    }, [])
    .map((i: any) => ({ ...i, roles: typeof i.roles === 'string' ? i.roles.split(",") : [] }));

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, allowedRoles }) => {
  const { user, sideNav, closeModal } = useSiteContext();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const currentPath = location.pathname;

  useEffect(() => {
    console.log("user " , user);
    // User not logged in case
    if (!user || Object.values(user).length === 0) {
      setSessionStorage(session_key, currentPath);
       navigate("/login");
      //showAlertAutoClose("Please Login", "error");
      closeModal("all");
      setIsAuthorized(false);
      return;
    }

    // Flatten navigation and get roles
    const flatNav = flattenNav(sideNav || []);
    const currentNavObj = flatNav.find((i: any) => i?.link === currentPath);

    const userRoles: string[] = Array.isArray(user?.roles) ? user.roles : [];
    
    // Determine access permission
    let permission = true;

    if (allowedRoles && allowedRoles.length > 0) {
      permission = userRoles.some(role => allowedRoles.includes(role));
    } else if (currentNavObj && Array.isArray(currentNavObj.roles)) {
      permission = userRoles.some(role => currentNavObj.roles.includes(role));
    }

    if (!permission) {
      showNotification1("error", "Restricted Access");
      navigate(-1);
      setIsAuthorized(false);
      return;
    }

    // Access granted
    setSessionStorage(session_key, "");
    setIsAuthorized(true);
  }, [user, sideNav, closeModal, navigate, allowedRoles, currentPath]);

  if (isAuthorized === null) {
    // While checking, you could render a loader or null to avoid flicker
    return null;
  }
  if (isAuthorized === false) {
    // Redirects or navigates happen in useEffect; here we return null to avoid rendering anything
    return null;
  }

  // Authorized: render the protected content
  return <>{children}</>;
};

export default React.memo(PrivateRoute);

